import { defineStore } from 'pinia';
import type { RewardFeedbackEvent, RewardFeedbackInput } from '@/types/gamification';

/**
 * Ephemeral reward feedback queue (M4C-29).
 *
 * Holds the transient star-born / fragments-gained / milestone events that drive
 * the reward modal and activity toasts for the *current UI session only*. Nothing
 * here is persisted: there is no reward feed, read/dismissed state or
 * acknowledgement endpoint (see M4C-25 / M4C-35). Events are emitted locally —
 * e.g. by `useGamificationService().emitRewardFeedback()` after a live action
 * response — and dropped once shown.
 *
 * A single queue feeds two surfaces, split by kind:
 *   • `star_born`                       → the blocking star-born modal (one at a
 *                                          time; `modalEvent` is the queue head).
 *   • `fragments_gained` / `milestone`  → non-blocking activity toasts (all shown
 *                                          at once, each auto-dismissed).
 * Both consume the same `events` array, so a single emit can drive either surface
 * and callers never need to know which one renders.
 */

// Module-local, monotonic id source. Transient by design: it resets on reload,
// which is fine because nothing references these ids across sessions.
let sequence = 0;

const isModalKind = (e: RewardFeedbackEvent): boolean => e.kind === 'star_born';

export const useRewardFeedbackStore = defineStore('rewardFeedback', () => {
  /** Source-of-truth queue of pending reward events. */
  const events = ref<RewardFeedbackEvent[]>([]);

  /** The star-born event currently shown by the modal (oldest first), else null. */
  const modalEvent = computed<RewardFeedbackEvent | null>(
    () => events.value.find(isModalKind) ?? null,
  );
  const hasModal = computed(() => modalEvent.value !== null);

  /** Every non-modal event, rendered together as a stack of activity toasts. */
  const toasts = computed<RewardFeedbackEvent[]>(() => events.value.filter((e) => !isModalKind(e)));

  /** Queue a new feedback event and return the stored copy (with its id). */
  function emit(input: RewardFeedbackInput): RewardFeedbackEvent {
    const event: RewardFeedbackEvent = { ...input, id: `rf-${++sequence}` };
    events.value.push(event);
    return event;
  }

  /** Remove a specific event (e.g. a dismissed/expired toast). */
  function dismiss(id: string): void {
    events.value = events.value.filter((e) => e.id !== id);
  }

  /** Dismiss the modal's current event — the next queued star-born takes its place. */
  function dismissModal(): void {
    if (modalEvent.value) {
      dismiss(modalEvent.value.id);
    }
  }

  /** Drop everything pending (e.g. on route change or logout). */
  function clear(): void {
    events.value = [];
  }

  return { events, modalEvent, hasModal, toasts, emit, dismiss, dismissModal, clear };
});
